import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDismissable } from '../useDismissable';
import { handleListNavKey } from '../listKeyboardNav';
import { MenuProps, MenuItem, MenuSection } from '../../../types/DesignSystem/Menu';
import Typography from '../Typography/Typography';
import Tooltip from '../Tooltip/Tooltip';
import { FOCUS_RING_INSET } from '../focusRing';
import { Check, ChevronRight } from '../Iconography/Icons';
import Kbd, { keysOf } from '../Kbd/Kbd';
import { withClassName } from '../cn';
import { useMergedRef } from '../useMergedRef';
import { splitAria } from '../ariaProps';
import { MOTION } from '../motion';
import { usePresence } from '../usePresence';
import { tabbablesIn } from '../tabbable';
import { POPOVER_ROOM_PX, fixedAnchorStyle, preferUpward, samePosition, useAnchorUpdates } from '../useAnchorUpdates';

/** § 386 — what only the anchored Menu hands its surface: the id its trigger's `aria-controls` names. */
interface MenuSurfaceInternals {
  menuId?: string;
}

/**
 * The menu SURFACE — the popover a caller positions (`Menu` without `trigger`, ~30 app files),
 * and what the anchored Menu renders beside its trigger (§ 386, below).
 */
const MenuSurface = React.forwardRef<HTMLDivElement, MenuProps & MenuSurfaceInternals>(function MenuSurface({
  items,
  sections,
  header,
  footer,
  onItemClick,
  variant = 'default',
  className = '',
  maxHeight,
  onDismiss,
  'data-testid': dataTestId,
  style: styleProp,
  menuId,
  ...restProps
}, forwardedRef) {
  /* § 384 — the primitive contract: the ref and native attributes reach the SURFACE (the root
     a caller positions), `aria-*` the `role="menu"` element it holds (`ariaProps.ts`). */
  const { aria, rest } = splitAria(restProps);
  const [expandedSubmenu, setExpandedSubmenu] = useState<string | number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const setContainerRef = useMergedRef<HTMLDivElement>(containerRef, forwardedRef);
  const uid = React.useId();

  /* `sections` and `items` normalise to one shape; a flat `items` list is a single
     unlabelled section. Dividers between sections are automatic. */
  const sectionList: MenuSection[] = sections ?? (items ? [{ items }] : []);
  const flatItems = sectionList.flatMap((section) => section.items);

  // Roving tabindex: exactly one item is in the tab order at a time, which is what
  // role="menu" promises. Seeded to the first enabled item and then driven by
  // `onFocus` below, so it follows real focus however focus arrived — arrow key,
  // click, or programmatically.
  const firstEnabledId = flatItems.find((item) => !item.disabled && !item.divider)?.id ?? null;
  const [rovingId, setRovingId] = useState<string | number | null>(firstEnabledId);
  const activeId = rovingId ?? firstEnabledId;

  useDismissable({
    isOpen: !!onDismiss,
    onDismiss: () => onDismiss?.(),
    isInside: (target) => !!containerRef.current?.contains(target),
  });

  /*
    § 344 — the submenu keys need to know, from the focused button alone, which item it
    is and whose child. Each row carries `data-menu-key` (its id as a string); these two
    maps answer the rest. Rebuilt per render, like `flatItems` above — menus are short.
  */
  const itemByKey = new Map<string, MenuItem>();
  const parentKeyOf = new Map<string, string>();
  const indexItems = (menuItems: MenuItem[], parentKey?: string) => {
    for (const item of menuItems) {
      const key = String(item.id);
      itemByKey.set(key, item);
      if (parentKey !== undefined) parentKeyOf.set(key, parentKey);
      if (item.submenu) indexItems(item.submenu, key);
    }
  };
  indexItems(flatItems);

  const rowFor = (key: string): HTMLElement | undefined =>
    [...(containerRef.current?.querySelectorAll<HTMLElement>('[data-menu-key]') ?? [])]
      .find((el) => el.dataset.menuKey === key);

  /* Focus that must land on a row that does not exist until the expansion renders. */
  const focusAfterRender = useRef<string | null>(null);
  useLayoutEffect(() => {
    const key = focusAfterRender.current;
    if (key === null) return;
    focusAfterRender.current = null;
    rowFor(key)?.focus();
  });

  /** Open `item`'s submenu and move focus to its first enabled child. */
  const enterSubmenu = (item: MenuItem) => {
    const first = item.submenu?.find((child) => !child.disabled && !child.divider);
    if (expandedSubmenu === item.id) {
      /* Already open: the child row exists now, and setting the same state again would
         not render, so a deferred focus would never land. */
      if (first) rowFor(String(first.id))?.focus();
      return;
    }
    setExpandedSubmenu(item.id);
    if (first) focusAfterRender.current = String(first.id);
  };

  /** Close the submenu under `parentKey` and put focus back on its row. */
  const leaveSubmenu = (parentKey: string) => {
    setExpandedSubmenu(null);
    rowFor(parentKey)?.focus();
  };

  const handleItemClick = (item: MenuItem, fromKeyboard = false) => {
    if (!item.disabled) {
      item.onClick?.();
      onItemClick?.(item.id);

      if (item.submenu) {
        const opening = expandedSubmenu !== item.id;
        /* § 344 — Enter/Space (a click with `detail` 0, the DropdownButton convention)
           opens AND enters, as a submenu does; a pointer click leaves focus where the
           pointer put it. */
        if (opening && fromKeyboard) enterSubmenu(item);
        else setExpandedSubmenu(opening ? item.id : null);
      }
    }
  };

  /*
    § 344 — the submenu keys. The parent used to announce `aria-haspopup="menu"`, whose
    promise is that ArrowRight enters it, and no key did anything. ArrowRight opens and
    enters; ArrowLeft leaves a child (or closes an open parent); Escape on a child
    closes just that submenu and stops there, so the menu's own dismissal waits for the
    next Escape. Up/Down stay with `handleListNavKey`: the children are rendered inline,
    so on screen they are the next rows and ArrowDown walks into them.
  */
  const handleSubmenuKey = (event: React.KeyboardEvent<HTMLDivElement>): boolean => {
    const key = (event.target as HTMLElement).closest<HTMLElement>('[data-menu-key]')?.dataset.menuKey;
    if (key === undefined) return false;
    const item = itemByKey.get(key);
    const parentKey = parentKeyOf.get(key);

    if (event.key === 'ArrowRight' && item?.submenu && !item.disabled) {
      event.preventDefault();
      enterSubmenu(item);
      return true;
    }
    if (event.key === 'ArrowLeft') {
      if (parentKey !== undefined) {
        event.preventDefault();
        leaveSubmenu(parentKey);
        return true;
      }
      if (item?.submenu && expandedSubmenu === item.id) {
        event.preventDefault();
        setExpandedSubmenu(null);
        return true;
      }
    }
    if (event.key === 'Escape' && parentKey !== undefined) {
      event.preventDefault();
      event.stopPropagation();
      leaveSubmenu(parentKey);
      return true;
    }
    return false;
  };

  /*
    `compact` was declared in the API from the first commit and never read — both
    variants rendered identically until § 65, when the delta-list migration needed
    the legacy toolbar menus' 28px rows. Compact IS that density now.
  */
  const itemPad = variant === 'compact' ? 'tw-py-1' : 'tw-py-2';
  const labelPad = variant === 'compact' ? 'tw-pt-1.5 tw-pb-0.5' : 'tw-pt-2 tw-pb-1';

  const renderMenuItems = (menuItems: MenuItem[], isSubmenu = false) => {
    return menuItems.map((item) => {
      const isExpanded = expandedSubmenu === item.id && item.submenu;
      /* `selected` defined at all makes the item a radio (§ 65) — aria-checked has
         to be present on BOTH states or the unchecked siblings read as plain
         actions and the group's "pick one" nature is lost. */
      const isRadio = item.selected !== undefined;
      /* § 247 — with a description the NAME must stay the label alone, so the button
         names itself by the label node and describes itself by the description node. */
      const domId = String(item.id).replace(/\s+/g, '-');
      const labelId = item.description ? `${uid}-${domId}-label` : undefined;
      const descriptionId = item.description ? `${uid}-${domId}-description` : undefined;
      /* § 344 — a parent row names its submenu group, and points at it while open. */
      const rowId = `${uid}-${domId}-row`;
      const groupId = `${uid}-${domId}-group`;
      /* The description's ink: recedes with a disabled row; on the accent selection
         tint (and the danger hover tint) tertiary ink falls under AA (§ 42), so it
         steps up to secondary there. */
      const descriptionInk = item.disabled
        ? 'tw-text-ink-disabled'
        : item.selected || item.tone === 'danger'
          ? 'tw-text-ink-secondary'
          : 'tw-text-ink-tertiary';

      const itemButton = (
        <button
          type="button"
          id={item.submenu ? rowId : undefined}
          data-menu-key={String(item.id)}
          onClick={(event) => handleItemClick(item, event.detail === 0)}
          onFocus={() => setRovingId(item.id)}
          /* § 403 — a disabled item is FOCUSABLE and refuses activation (`handleItemClick`
             checks): `aria-disabled`, not the native attribute. Natively disabled, the arrows
             passed over it, so a keyboard or screen-reader user could not learn it existed,
             and its tooltip, usually the REASON it is disabled (ISR's row Delete, Products'
             bulk Recategorize), opened for a pointer only. The APG menu rule. Opening the
             menu still lands on the first ENABLED item, and so does Tab into it. */
          aria-disabled={item.disabled || undefined}
          role={isRadio ? 'menuitemradio' : 'menuitem'}
          aria-checked={isRadio ? item.selected : undefined}
          aria-keyshortcuts={item.shortcut || undefined}
          aria-labelledby={labelId}
          aria-describedby={descriptionId}
          /* § 302 — a row that opens a submenu SAYS so. The chevron beside it is
             aria-hidden, so without this the row announced as a plain menuitem and a
             screen-reader user had no way to know it held more. Absent on ordinary rows.
             § 344 — and says it TRUTHFULLY. `aria-haspopup="menu"` promised a popup menu
             that ArrowRight enters; the submenu expands inline under the row. So the row
             is an expandable item: `aria-expanded` is the fact, `aria-controls` points at
             the group it opens, and the submenu keys are handled below. */
          aria-expanded={item.submenu ? Boolean(isExpanded) : undefined}
          aria-controls={item.submenu && isExpanded ? groupId : undefined}
          tabIndex={item.id === activeId ? 0 : -1}
          data-testid={item['data-testid']}
          className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 ${itemPad} tw-text-left tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift ${FOCUS_RING_INSET} ${
            isSubmenu ? 'tw-pl-6' : ''
          } ${
            item.disabled
              /* § 228 — ONE recede. This stacked `tw-opacity-50` on `ink-disabled`,
                 halving the system's chosen disabled value to ~1.4:1 — the doubled
                 dimming § 123 (Toggle) and § 227 (Checkbox/Radio) rooted out. The
                 token IS the recede; opacity multiplied it.
                 § 403 — and the system's disabled ink under forced colors: a natively disabled
                 button gets GrayText from the browser there, an `aria-disabled` one does not, so
                 it would read as enabled in Windows High Contrast (seen in the browser suite). */
              ? 'tw-cursor-not-allowed tw-text-ink-disabled forced-colors:tw-text-[color:GrayText]'
              : item.selected
                /* The ONE selection grammar (§ 51/§ 65): accent on its tint, the same
                   pair a Dropdown option wears when chosen. Hover stays neutral. */
                ? 'tw-bg-accent-tint tw-text-accent tw-cursor-pointer'
                : item.tone === 'danger'
                  /* Destructive actions carry the danger tone rather than the neutral
                     ink — see `tone` in the type file. Hover moves to the tone's own
                     tint so the row does not fall back to a neutral ground mid-gesture. */
                  ? 'hover:tw-bg-tone-danger-tint tw-text-tone-danger-ink tw-cursor-pointer'
                  /* Same rendered hexes as before — role spellings so all three
                     menus (Menu, Dropdown options, DropdownButton rows) share ONE
                     hover ground by name (§ 51). */
                  : 'hover:tw-bg-surface-hover-strong active:tw-bg-tone-neutral-tint tw-text-ink tw-cursor-pointer'
          }`}
        >
          {item.icon && <span className="tw-flex-shrink-0">{item.icon}</span>}
          <span className="tw-flex-1 tw-min-w-0">
            {/* § 346 — a `span` (kept `block`): `body-primary` defaults to `<p>`, and a
                `<p>` is flow content, which a `<button>` may not contain. */}
            <Typography variant="body-primary" as="span" id={labelId} className="tw-block">
              {item.label}
            </Typography>
            {item.description && (
              <Typography
                variant="caption-metadata"
                as="span"
                id={descriptionId}
                className={`tw-block tw-mt-0.5 ${descriptionInk}`}
                data-testid={item['data-testid'] ? `${item['data-testid']}-description` : undefined}
              >
                {item.description}
              </Typography>
            )}
          </span>
          {item.shortcut && (
            /* aria-hidden: `aria-keyshortcuts` on the row is the announced fact; the
               caps are its picture. `ml-2` keeps a long label off the first cap. */
            <span
              aria-hidden="true"
              className="tw-flex tw-items-center tw-gap-1 tw-shrink-0 tw-ml-2"
              data-testid={item['data-testid'] ? `${item['data-testid']}-shortcut` : undefined}
            >
              {keysOf(item.shortcut).map((key, index) => (
                // eslint-disable-next-line react/no-array-index-key -- a chord can legitimately repeat a key
                <Kbd key={`${key}-${index}`}>{key}</Kbd>
              ))}
            </span>
          )}
          {isRadio && item.selected && (
            /* § 378 — the checked row's MARK: the set's Check at the size a chosen Dropdown
               option draws it. The tint alone was the only sign, and a fill is exactly what
               forced colors takes away. `aria-hidden` — `aria-checked` is the fact. */
            <Check
              size={12}
              aria-hidden="true"
              className="tw-ml-2 tw-flex-shrink-0"
              data-testid={item['data-testid'] ? `${item['data-testid']}-check` : undefined}
            />
          )}
          {item.submenu && (
            /* § 232 — the set's ChevronRight, not a private 24-grid/stroke-2 drawing:
               the same glyph the table's expand chevron rotates, at the family mark
               weight. `aria-hidden` — aria-expanded on the row is the fact. */
            <ChevronRight
              size={16}
              aria-hidden="true"
              className={`tw-flex-shrink-0 motion-safe:tw-transition-transform motion-safe:tw-duration-base motion-safe:tw-ease-swift ${
                isExpanded ? 'tw-rotate-90' : ''
              }`}
            />
          )}
        </button>
      );

      return (
        <div key={item.id}>
          {item.divider ? (
            /* § 344 — a separator, like the one `sections` draw; inside `role="menu"` a
               bare div was an unnamed generic between two items. */
            <div className="tw-my-1 tw-border-t tw-border-edge" role="separator" />
          ) : item.tooltip ? (
            /* Same shape as Dropdown option tooltips: the tooltip wraps the button,
               so it still opens when the button is `disabled` (a disabled element
               fires no pointer events — the wrapper does). `usePortal` because the
               menu may scroll. */
            <Tooltip content={item.tooltip} placement="right" usePortal block>
              {itemButton}
            </Tooltip>
          ) : (
            itemButton
          )}

          {item.submenu && isExpanded && (
            /* § 296 — `surface-recess`: this popover is `raised`, and in dark
               `surface-sunken` is the page ground, so an expanded submenu was a
               hole punched through the menu (−8.7 L*). One rung down, both themes. */
            /* § 344 — a `group` named by its parent row: the children are that row's,
               which the flat list alone could not say. */
            <div className="tw-bg-surface-recess" role="group" id={groupId} aria-labelledby={rowId}>
              {renderMenuItems(item.submenu, true)}
            </div>
          )}
        </div>
      );
    });
  };

  /*
    Two-layer structure (§ 65): the OUTER div owns the popover chrome, the caller's
    className/testid, and scrolling; the INNER div is the `role="menu"`. The split
    exists for `header`/`footer` — ARIA allows only items, groups and separators
    inside a menu, so anything else (an identity block, a SegmentedControl, a hint)
    must sit beside the menu, not in it. Keyboard handling lives on the menu element
    so those widgets keep their own arrow keys and never join the item roving order.
  */
  return (
    <div
      {...rest}
      ref={setContainerRef}
      /*
        § 219 — the popover chrome the OTHER two popovers already wore. Dropdown's
        listbox and DropdownButton's menu carry `shadow-ds-md` + `popup-rise`;
        Menu — the same floating surface, mounted by ~20 app call sites — had a
        bare border, so the app's row-action and profile menus rendered flat and
        arrived with no entrance. Surveyed 2026-09-07: every consumer wraps Menu
        in an absolute/fixed positioned box (none inline), so the elevation is
        unconditional. `origin-top` makes the 0.98 scale grow OUT of the trigger.
        (Searchbar passes `tw-shadow-ds-md` in className — now a harmless
        duplicate of the same class, to be dropped when that file is next touched.)
      */
      /* `overscroll-contain` (§ 222) is inert until `maxHeight` makes this the
         scroller, and then keeps the wheel from chaining to the page. */
      className={withClassName(`tw-bg-surface-raised tw-rounded-srf tw-border tw-border-edge tw-shadow-ds-md tw-origin-top motion-safe:tw-animate-popup-rise tw-overflow-hidden tw-overscroll-contain`, className)}
      style={maxHeight ? { ...styleProp, maxHeight, overflowY: 'auto' } : styleProp}
      data-testid={dataTestId}
    >
      {/*
        `text-ink` on BOTH slots (2026-08-30). The menu ITEMS set their own ink, so they
        were readable in dark while anything composed into these slots inherited from
        outside the popover — which is nothing, i.e. the browser's black. The GMS profile
        menu shipped an identity block whose name, email and role row were invisible on a
        dark ground. § 55's slot-ink rule, one level up: a DS surface owes its content
        slots an ink, because a caller passing plain `<Typography>` has no way to know it
        is landing on an unpainted surface.
      */}
      {header && <div className="tw-border-b tw-border-edge tw-text-ink">{header}</div>}
      <div
        {...aria}
        id={menuId}
        role="menu"
        onKeyDown={(event) => {
          if (handleSubmenuKey(event)) return;
          handleListNavKey(event, {
            container: event.currentTarget,
            // Submenu items are rendered inline, so they join the navigation order
            // as soon as their parent expands — which is why the item list is
            // re-read on every keypress rather than captured once.
            itemSelector: '[role="menuitem"], [role="menuitemradio"]',
            orientation: 'vertical',
            typeAhead: true,
            /* § 403 — the arrows reach a disabled item; only activation is refused. */
            skipDisabled: false,
          });
        }}
      >
        {sectionList.map((section, sectionIndex) => {
          const labelId = section.label ? `${uid}-section-${sectionIndex}` : undefined;
          return (
            <React.Fragment key={sectionIndex}>
              {sectionIndex > 0 && <div className="tw-my-1 tw-border-t tw-border-edge" role="separator" />}
              <div role="group" aria-labelledby={labelId} data-testid={section['data-testid']}>
                {section.label && (
                  <div id={labelId} className={`tw-px-3 ${labelPad}`}>
                    <Typography variant="sub-title-label" className="tw-text-ink-tertiary">
                      {section.label}
                    </Typography>
                  </div>
                )}
                {renderMenuItems(section.items)}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {footer && <div className="tw-border-t tw-border-edge tw-p-2 tw-text-ink">{footer}</div>}
    </div>
  );
});

/* § 403 — where a keyboard OPEN lands: the first or last ENABLED item. Disabled items are
   `aria-disabled` (focusable, reachable by the arrows) since § 403. */
const ITEM_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"]), [role="menuitemradio"]:not([aria-disabled="true"])';

/** Every item, submenus included — to tell a submenu parent (which opens) from a pick (which closes). */
const flattenItems = (list: MenuItem[]): MenuItem[] =>
  list.flatMap((item) => (item.submenu ? [item, ...flattenItems(item.submenu)] : [item]));

/**
 * § 386 — the Menu that anchors itself to `trigger`: the APG menu-button contract DropdownButton
 * has (§ 304), with Menu's own item model. The surface is MenuSurface, unchanged; this layer
 * owns what a caller used to hand-roll — the open state, the fixed placement beside the trigger
 * (`fixedAnchorStyle`, the § 334 engine every anchored surface shares), the keyboard and focus.
 */
const AnchoredMenu = React.forwardRef<HTMLDivElement, MenuProps & { trigger: React.ReactElement }>(function AnchoredMenu({
  trigger,
  open: openProp,
  onOpenChange,
  align = 'start',
  onItemClick,
  onDismiss,
  className = '',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...surfaceProps
}, forwardedRef) {
  const uid = React.useId();
  const menuId = `${uid}-menu`;
  const triggerProps = trigger.props as {
    id?: string;
    onClick?: (event: React.MouseEvent) => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
  };
  const triggerId = triggerProps.id ?? `${uid}-trigger`;

  /* Controlled when `open` is passed; otherwise the menu keeps its own state. */
  const [openState, setOpenState] = useState(false);
  const isOpen = openProp !== undefined ? openProp : openState;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  const anchorRef = useRef<HTMLSpanElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const setSurfaceRef = useMergedRef<HTMLDivElement>(surfaceRef, forwardedRef);
  const { isPresent, phase } = usePresence(isOpen, MOTION.popover.exitMs);
  const exiting = phase === 'exiting';
  const [openUp, setOpenUp] = useState(false);
  const [placement, setPlacement] = useState<React.CSSProperties | null>(null);
  const measure = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const above = preferUpward(rect, POPOVER_ROOM_PX);
    setOpenUp(above);
    const next = fixedAnchorStyle(rect, {
      above,
      align,
      fit: true,
      floatingWidth: surfaceRef.current?.offsetWidth,
      floatingHeight: surfaceRef.current?.scrollHeight,
      anchorEl: anchorRef.current,
    });
    setPlacement((prev) => (prev && samePosition(prev, next) ? prev : next));
  }, [align]);
  useAnchorUpdates(isPresent && !exiting, anchorRef, measure, surfaceRef);
  const mounted = isPresent && placement !== null;
  /* Measure again once the surface exists (its width and height enter the placement), and
     place a menu opened by the caller's `open` rather than by the trigger. */
  useLayoutEffect(() => {
    if (mounted) measure();
  }, [mounted, measure]);
  useEffect(() => {
    if (isOpen && placement === null) measure();
  }, [isOpen, placement, measure]);

  /* A keyboard open lands on an item once the surface is in the DOM. */
  const pendingFocus = useRef<'first' | 'last' | null>(null);
  const focusItem = (which: 'first' | 'last') => {
    const items = surfaceRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR);
    if (!items || items.length === 0) return;
    (which === 'first' ? items[0] : items[items.length - 1]).focus();
  };
  useEffect(() => {
    /* Only while OPEN: the surface stays mounted through its exit animation, and a pending
       focus left over from an open that closed before it landed (Escape pressed straight
       after ArrowDown — caught in Chromium) must not pull focus back into a closing menu. */
    if (!mounted || !isOpen || pendingFocus.current === null) return;
    focusItem(pendingFocus.current);
    pendingFocus.current = null;
  });

  const focusTrigger = () => {
    const anchor = anchorRef.current;
    if (anchor) (tabbablesIn(anchor)[0] ?? (anchor.firstElementChild as HTMLElement | null))?.focus();
  };
  /* Close, handing focus back to the trigger when it was in the menu (or when asked). */
  const close = (returnFocus: boolean) => {
    pendingFocus.current = null;
    const inMenu = !!surfaceRef.current?.contains(document.activeElement);
    setOpen(false);
    if (returnFocus || inMenu) focusTrigger();
  };
  const open = (focus?: 'first' | 'last') => {
    pendingFocus.current = focus ?? null;
    measure();
    setOpen(true);
  };

  useDismissable({
    isOpen,
    onDismiss: (reason) => {
      close(reason === 'escape');
      onDismiss?.();
    },
    isInside: (target) => !!anchorRef.current?.contains(target) || !!surfaceRef.current?.contains(target),
  });

  const allItems = flattenItems(surfaceProps.sections?.flatMap((section) => section.items) ?? surfaceProps.items ?? []);
  const handlePick = (id: string | number) => {
    onItemClick?.(id);
    const item = allItems.find((candidate) => candidate.id === id);
    if (!item?.submenu) close(false);
  };

  const triggerEl = React.cloneElement(trigger as React.ReactElement<Record<string, unknown>>, {
    id: triggerId,
    'aria-haspopup': 'menu',
    'aria-expanded': isOpen,
    'aria-controls': isOpen ? menuId : undefined,
    onClick: (event: React.MouseEvent) => {
      triggerProps.onClick?.(event);
      if (event.defaultPrevented) return;
      if (isOpen) {
        close(false);
        return;
      }
      /* `detail === 0` is a keyboard activation (Enter / Space): open on the first item. */
      open(event.detail === 0 ? 'first' : undefined);
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      triggerProps.onKeyDown?.(event);
      if (event.defaultPrevented || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
      event.preventDefault();
      const which = event.key === 'ArrowDown' ? 'first' : 'last';
      if (isOpen) focusItem(which);
      else open(which);
    },
  });

  return (
    <span ref={anchorRef} className="tw-relative tw-inline-flex">
      {triggerEl}
      {mounted && createPortal(
        <MenuSurface
          {...surfaceProps}
          ref={setSurfaceRef}
          menuId={menuId}
          /* The trigger names the menu, unless the caller names it. */
          aria-labelledby={ariaLabelledBy ?? (ariaLabel ? undefined : triggerId)}
          aria-label={ariaLabel}
          aria-hidden={exiting || undefined}
          /* The marker Dialog's outside-click guard and the modal Tab cycle read (§ 304). */
          data-dropdown-portal={uid}
          style={placement ?? undefined}
          className={withClassName(`${openUp ? 'tw-origin-bottom' : 'tw-origin-top'}${exiting ? ` ${MOTION.popover.exit} tw-pointer-events-none` : ''}`, className)}
          onItemClick={handlePick}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            /* Tab leaves the MENU, not the page: close and return to the trigger, so the
               next Tab moves on from there rather than from the end of <body>. */
            if (event.key !== 'Tab') return;
            event.preventDefault();
            close(true);
          }}
        />,
        document.body,
      )}
    </span>
  );
});

/**
 * `Menu` — the caller-positioned surface, or (§ 386) with `trigger`, a menu that anchors itself.
 */
const Menu = React.forwardRef<HTMLDivElement, MenuProps>(function Menu(props, ref) {
  if (props.trigger) return <AnchoredMenu {...props} trigger={props.trigger} ref={ref} />;
  /* The anchoring props mean nothing without a trigger; they must not reach the surface's DOM. */
  const { trigger, open, onOpenChange, align, ...surfaceProps } = props;
  void trigger; void open; void onOpenChange; void align;
  return <MenuSurface {...surfaceProps} ref={ref} />;
});

export default Menu;
